import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import Loading from '../../../components/common/loading';
import RowList from '../../../components/common/rowList';
import TextInput from '../../../components/common/inputs/textInput.jsx';
import ToggleInput from '../../../components/common/inputs/toggleInput.jsx';
import NotificationManager from '../../../utils/notificationManager';
import { isUnknownApiEndpointError } from '../../../utils/networkErrorUtils';

const defaultForm = {
  checkInterval: '',
  failureThreshold: '',
  recoveryThreshold: '',
  defaultHealthTarget: '',
};

const styles = {
  card: {
    padding: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '12px',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: 600,
    color: '#1f2d3d',
  },
  description: {
    marginTop: '8px',
    color: '#607284',
    fontSize: '14px',
  },
  banner: {
    marginTop: '16px',
    padding: '12px 16px',
    borderRadius: 6,
    fontSize: '13px',
  },
  warningBanner: {
    background: '#fef3c7',
    color: '#92400e',
  },
  infoBanner: {
    background: '#e0f2fe',
    color: '#075985',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  subTitle: {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2d3d',
    marginTop: "10px"
  },
  formLayout: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '20px',
    marginTop: '24px',
  },
  metricsCard: {
    flex: '1 1 280px',
    minWidth: '260px',
  },
  formCard: {
    flex: '1 1 420px',
    minWidth: '320px',
  },
  routesWrapper: {
    marginTop: '24px',
  },
  errorText: {
    color: '#b91c1c',
    marginTop: '12px',
    fontSize: '13px',
  },
  loadingWrap: {
    padding: '24px',
    display: 'flex',
    justifyContent: 'center',
  },
};

const extractConfigFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return {};
  }
  if (payload.config && typeof payload.config === 'object' && !Array.isArray(payload.config)) {
    return payload.config;
  }
  return payload;
};

const extractRoutesFromPayload = (payload) => {
  if (!payload || typeof payload !== 'object') {
    return [];
  }
  if (Array.isArray(payload.routes)) {
    return payload.routes;
  }
  const config = extractConfigFromPayload(payload);
  if (Array.isArray(config.routes)) {
    return config.routes;
  }
  return [];
};

const formatTimestamp = (epochSeconds) => {
  if (!epochSeconds || Number.isNaN(Number(epochSeconds))) {
    return '—';
  }
  const date = new Date(Number(epochSeconds) * 1000);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
};

const WAN_HEALTH_UNSUPPORTED_MESSAGE =
  'Update the PDU firmware to enable WAN health monitoring and configuration from Synaccess Cloud.';

export default function DeviceConfigurationWanHealth({
  device,
  user,
  deviceOffline,
}) {
  const serial = device?.enclosureSerialNumber;
  const [wanHealth, setWanHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formValues, setFormValues] = useState(defaultForm);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [featureUnavailable, setFeatureUnavailable] = useState(false);
  const [featureMessage, setFeatureMessage] = useState('');
  const [initializing, setInitializing] = useState(true);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    }),
    []
  );

  const hasEditPermission = (user?.role?.editDeviceInfo ?? true) !== false;
  const canModify = hasEditPermission && !deviceOffline && !featureUnavailable;

  const fetchWanHealth = useCallback(async () => {
    if (!serial) {
      setInitializing(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(
        `/v1/devices/${serial}/network/wan-health`,
        { headers: authHeaders }
      );
      setWanHealth(data?.data || {});
      setFeatureUnavailable(false);
      setFeatureMessage('');
    } catch (err) {
      console.error(err);
      if (isUnknownApiEndpointError(err)) {
        setFeatureUnavailable(true);
        setFeatureMessage(WAN_HEALTH_UNSUPPORTED_MESSAGE);
        setWanHealth(null);
        setError('');
        return;
      }
      const message =
        err?.response?.data?.message ||
        'Unable to retrieve WAN health status from the device.';
      setError(message);
      NotificationManager.alert(message);
    } finally {
      setLoading(false);
      setInitializing(false);
    }
  }, [serial, authHeaders]);

  useEffect(() => {
    fetchWanHealth();
  }, [fetchWanHealth]);

  useEffect(() => {
    const config = extractConfigFromPayload(wanHealth);
    if (!config) {
      return;
    }
    setFormValues({
      checkInterval:
        config.checkInterval !== undefined ? String(config.checkInterval) : '',
      failureThreshold:
        config.failureThreshold !== undefined ? String(config.failureThreshold) : '',
      recoveryThreshold:
        config.recoveryThreshold !== undefined ? String(config.recoveryThreshold) : '',
      defaultHealthTarget:
        config.defaultHealthTarget !== undefined ? String(config.defaultHealthTarget) : '',
    });
    setFormErrors({});
  }, [wanHealth]);

  const handleRefresh = () => {
    if (!loading) {
      fetchWanHealth();
    }
  };

  const handleFormChange = (field, value) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [field]: '',
      general: '',
    }));
  };

  const validateForm = () => {
    const errors = {};
    const checkInterval = Number(formValues.checkInterval);
    if (
      Number.isNaN(checkInterval) ||
      checkInterval < 10 ||
      checkInterval > 3600
    ) {
      errors.checkInterval = 'Check interval must be between 10 and 3600 seconds.';
    }
    const failureThreshold = Number(formValues.failureThreshold);
    if (
      Number.isNaN(failureThreshold) ||
      failureThreshold < 1 ||
      failureThreshold > 10
    ) {
      errors.failureThreshold = 'Failure threshold must be between 1 and 10.';
    }
    const recoveryThreshold = Number(formValues.recoveryThreshold);
    if (
      Number.isNaN(recoveryThreshold) ||
      recoveryThreshold < 1 ||
      recoveryThreshold > 10
    ) {
      errors.recoveryThreshold = 'Recovery threshold must be between 1 and 10.';
    }
    if (!formValues.defaultHealthTarget || !formValues.defaultHealthTarget.trim()) {
      errors.defaultHealthTarget = 'Default health target is required.';
    }
    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!serial || !canModify || submitting) {
      return;
    }
    const errors = validateForm();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    const payload = {
      checkInterval: Number(formValues.checkInterval),
      failureThreshold: Number(formValues.failureThreshold),
      recoveryThreshold: Number(formValues.recoveryThreshold),
      defaultHealthTarget: formValues.defaultHealthTarget.trim(),
    };
    setSubmitting(true);
    try {
      await axios.post(
        `/v1/devices/${serial}/network/wan-health/config`,
        payload,
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
      );
      NotificationManager.success('WAN health configuration updated.');
      fetchWanHealth();
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        'Failed to update the WAN health configuration.';
      setFormErrors({ general: message });
      NotificationManager.alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleMonitoring = async (enabled) => {
    if (!serial || !canModify || toggling) {
      return;
    }
    setToggling(true);
    try {
      await axios.post(
        `/v1/devices/${serial}/network/wan-health/monitoring`,
        { enabled },
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
      );
      NotificationManager.success(
        `WAN health monitoring ${enabled ? 'enabled' : 'disabled'}.`
      );
      fetchWanHealth();
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.message ||
        'Failed to update the monitoring state.';
      NotificationManager.alert(message);
    } finally {
      setToggling(false);
    }
  };

  const config = extractConfigFromPayload(wanHealth);
  const routes = extractRoutesFromPayload(wanHealth);
  const monitoringEnabled = !!config.monitoringEnabled;
  const currentTarget =
    config.currentTarget ||
    config.activeTarget ||
    config.defaultHealthTarget ||
    '—';
  const healthyCount = routes.filter((route) => route.healthy).length;
  const unhealthyCount =
    routes.length > 0 ? routes.length - healthyCount : 0;

  const monitoringRows = [
    {
      label: 'Monitoring Status',
      value: monitoringEnabled ? 'Enabled' : 'Disabled',
      valueStyle: {
        color: monitoringEnabled ? 'forestgreen' : 'maroon',
        fontWeight: 600,
      },
    },
    {
      label: 'Current Target',
      value: currentTarget,
    },
    {
      label: 'Route Health',
      value: `${healthyCount} Healthy / ${unhealthyCount} Unhealthy`,
      valueStyle: {
        color: unhealthyCount > 0 ? 'maroon' : 'forestgreen',
        fontWeight: 600,
      },
    },
  ];

  const renderRouteRow = (route) => {
    const lastCheck = formatTimestamp(route.lastCheck);
    const stateLabel = route.healthy ? 'Healthy' : 'Unhealthy';
    const stateColor = route.healthy ? 'forestgreen' : 'maroon';
    const key = `${route.interface || 'iface'}-${route.gateway || 'gw'}-${
      route.priority !== undefined ? route.priority : 'prio'
    }`;
    return (
      <div className="grid-row-wrapper" key={key}>
        <div className="grid-row large-only">
          <div style={{ flex: 3 }}>{route.interface || '—'}</div>
          <div style={{ flex: 4 }}>{route.gateway || '—'}</div>
          <div style={{ flex: 2 }}>{route.priority !== undefined ? route.priority : '—'}</div>
          <div style={{ flex: 3, color: stateColor, fontWeight: 600 }}>{stateLabel}</div>
          <div style={{ flex: 3 }}>{route.successCount !== undefined ? route.successCount : '—'}</div>
          <div style={{ flex: 3 }}>{route.failureCount !== undefined ? route.failureCount : '—'}</div>
          <div style={{ flex: 4 }}>{lastCheck}</div>
        </div>
        <div className="small-only" style={{ background: '#fff', padding: '10px', border: '1px solid rgba(0,0,0,0.1)', borderTop: 'none' }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {route.interface || '—'}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>Gateway:</strong> {route.gateway || '—'}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>Priority:</strong>{' '}
            {route.priority !== undefined ? route.priority : '—'}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>Status:</strong>{' '}
            <span style={{ color: stateColor, fontWeight: 600 }}>
              {stateLabel}
            </span>
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>Success Count:</strong>{' '}
            {route.successCount !== undefined ? route.successCount : '—'}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>Failure Count:</strong>{' '}
            {route.failureCount !== undefined ? route.failureCount : '—'}
          </div>
          <div style={{ fontSize: '90%', marginBottom: 4 }}>
            <strong>Last Check:</strong> {lastCheck}
          </div>
        </div>
      </div>
    );
  };

  if (initializing) {
    return (
      <div style={{ ...styles.card, ...styles.loadingWrap }}>
        <Loading black />
      </div>
    );
  }

  if (featureUnavailable) {
    return (
      <div style={{ ...styles.card, ...styles.infoBanner }}>
        {featureMessage || WAN_HEALTH_UNSUPPORTED_MESSAGE}
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <h4 style={styles.title}>WAN Health</h4>
          <p style={styles.description}>
            Monitor WAN connectivity and automatically fail over between interfaces.
          </p>
        </div>
        <button
          type="button"
          className="button secondary"
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {deviceOffline && (
        <div style={{ ...styles.banner, ...styles.warningBanner }}>
          Device is offline. Configuration changes are disabled until it reconnects.
        </div>
      )}

      {error && (
        <div className="alert alert-warning" style={{ marginTop: '16px' }}>
          {error}
        </div>
      )}

      {loading && !wanHealth ? (
        <div style={{ marginTop: '24px' }}>
          <Loading />
        </div>
      ) : (
        <>
          <div style={styles.routesWrapper}>
            <div style={styles.sectionHeader}>
              <h5 style={styles.subTitle}>Monitored Routes</h5>
            </div>
            {routes.length === 0 ? (
              <div className="app-item" style={{ marginTop: '12px' }}>
                No WAN routes are currently tracked for health checks.
              </div>
            ) : (
              <div className="app-item" style={{ marginTop: '12px', padding: 0, overflowX: 'auto' }}>
                <div className="grid-row header large-only">
                  <div style={{ flex: 3 }}>Interface</div>
                  <div style={{ flex: 4 }}>Gateway</div>
                  <div style={{ flex: 2 }}>Priority</div>
                  <div style={{ flex: 3 }}>State</div>
                  <div style={{ flex: 3 }}>Successes</div>
                  <div style={{ flex: 3 }}>Failures</div>
                  <div style={{ flex: 4 }}>Last Check</div>
                </div>
                {routes.map(renderRouteRow)}
              </div>
            )}
          </div>

          <div style={styles.formLayout}>
            <div style={styles.metricsCard}>
              <div style={styles.sectionHeader}>
                <h5 style={styles.subTitle}>Monitoring Overview</h5>
              </div>
              <div style={{ marginTop: '12px' }}>
                <RowList rows={monitoringRows} />
              </div>
            </div>

            <form
              style={styles.formCard}
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              <div style={styles.sectionHeader}>
                <h5 style={styles.subTitle}>Global Configuration</h5>
                <button
                  type="submit"
                  className="button"
                  style={{
                    marginBottom: "0px"
                  }}
                  disabled={!canModify || submitting}
                >
                  {submitting ? 'Saving…' : 'Save'}
                </button>
              </div>
              {!deviceOffline && !hasEditPermission && (
                <div style={{ ...styles.banner, ...styles.warningBanner, marginTop: '12px' }}>
                  You do not have permission to modify device configuration.
                </div>
              )}
              <div className="app-item" style={{ marginTop: '10px' }}>
                <ToggleInput
                  label="Monitoring Control"
                  value={monitoringEnabled}
                  callback={(value) => handleToggleMonitoring(value)}
                  disabled={!canModify || toggling}
                  statusText={(value) =>
                    toggling ? 'Applying…' : value ? 'Enabled' : 'Disabled'
                  }
                  inputContainerStyle={{ marginBottom: '12px' }}
                />
                <TextInput
                  label="Check Interval (10-3600 seconds)"
                  type="number"
                  min={10}
                  max={3600}
                  value={formValues.checkInterval}
                  callback={(value) =>
                    handleFormChange(
                      'checkInterval',
                      value === '' ? '' : String(value)
                    )
                  }
                  disabled={!canModify}
                  errMsg={formErrors.checkInterval}
                  inputContainerStyle={{ marginBottom: '12px' }}
                />
                <TextInput
                  label="Failure Threshold (1-10)"
                  type="number"
                  min={1}
                  max={10}
                  value={formValues.failureThreshold}
                  callback={(value) =>
                    handleFormChange(
                      'failureThreshold',
                      value === '' ? '' : String(value)
                    )
                  }
                  disabled={!canModify}
                  errMsg={formErrors.failureThreshold}
                  inputContainerStyle={{ marginBottom: '12px' }}
                />
                <TextInput
                  label="Recovery Threshold (1-10)"
                  type="number"
                  min={1}
                  max={10}
                  value={formValues.recoveryThreshold}
                  callback={(value) =>
                    handleFormChange(
                      'recoveryThreshold',
                      value === '' ? '' : String(value)
                    )
                  }
                  disabled={!canModify}
                  errMsg={formErrors.recoveryThreshold}
                  inputContainerStyle={{ marginBottom: '12px' }}
                />
                <TextInput
                  label="Default Health Target"
                  placeholder="8.8.8.8"
                  value={formValues.defaultHealthTarget}
                  callback={(value) =>
                    handleFormChange('defaultHealthTarget', value)
                  }
                  disabled={!canModify}
                  errMsg={formErrors.defaultHealthTarget}
                />
                {formErrors.general && (
                  <div style={styles.errorText}>{formErrors.general}</div>
                )}
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
